// src/controllers/optimizadorLP.js
// Optimiza la dieta con variables a_i = kg as-fed/día por materia.
// Restricciones del Paso 4 en KG usados (as-fed): minKg/maxKg
// Objetivo: minimizar costo cumpliendo requerimientos.

const toNum = (v) => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
};
const get = (obj, k, def = 0) => {
    const v = toNum(obj?.valores?.[k]);
    return v !== undefined ? v : def;
};
const MSfr = (m) => {
    const ms = toNum(m?.valores?.MS);
    return ms !== undefined ? ms / 100 : 0;
};

// -------- Simplex: Min c^T x  s.a. A x <= b, x>=0  -------
function simplexMin(A, b, c) {
    const m = A.length, n = (A[0]?.length || 0);
    const N = n + m;
    const T = Array(m + 1).fill(0).map(() => Array(N + 1).fill(0));

    for (let i = 0; i < m; i++) {
        for (let j = 0; j < n; j++) T[i][j] = A[i][j];
        T[i][n + i] = 1;
        T[i][N] = b[i];
    }
    for (let j = 0; j < n; j++) T[m][j] = -c[j];

    const base = Array(m).fill(0).map((_, i) => n + i);

    function pivot(row, col) {
        const piv = T[row][col];
        for (let j = 0; j <= N; j++) T[row][j] /= piv;
        for (let i = 0; i <= m; i++) {
            if (i === row) continue;
            const f = T[i][col];
            for (let j = 0; j <= N; j++) T[i][j] -= f * T[row][j];
        }
        base[row] = col;
    }

    while (true) {
        let col = -1;
        for (let j = 0; j < N; j++) if (T[m][j] < -1e-9) { col = j; break; }
        if (col === -1) break;

        let row = -1, best = Infinity;
        for (let i = 0; i < m; i++) {
            const a = T[i][col];
            if (a > 1e-9) {
                const ratio = T[i][N] / a;
                if (ratio < best - 1e-12) { best = ratio; row = i; }
            }
        }
        if (row === -1) return { ok: false, msg: "Problema no acotado", x: Array(n).fill(0) };
        pivot(row, col);
    }

    const x = Array(n).fill(0);
    for (let i = 0; i < m; i++) {
        const bi = base[i];
        if (bi < n) x[bi] = T[i][N];
    }
    return { ok: true, msg: "OK", x };
}
function addLeq(constrA, constrB, row) {
    constrA.push(row.A);
    constrB.push(row.b);
}
function addGeq(constrA, constrB, row) {
    const Aneg = row.A.map(v => -v);
    addLeq(constrA, constrB, { A: Aneg, b: -row.b });
}

// ------------- Construcción del LP --------------
function optimizarDietaLP(mats, restrRows, reqNutricional) {
    const n = mats.length;
    if (!n) return { ok: false, msg: "Sin materias" };

    const restrById = {};
    for (const r of (restrRows || [])) {
        if (!r) continue;
        const id = r.idMateria ?? r.id ?? r.materiaId;
        if (id == null) continue;
        restrById[String(id)] = r;
    }

    const getCosto = (id) => {
        const r = restrById[String(id)] || {};
        return toNum(r.costo ?? r.costoKg ?? r.precio ?? r.valor_unitario) ?? 0;
    };

    const rowsReq = Array.isArray(reqNutricional?.rows) ? reqNutricional.rows : [];
    const rAsFed = rowsReq.find(r => String(r.id) === "1");
    const ASFED_MIN = rAsFed?.min !== undefined ? toNum(rAsFed.min) : undefined;
    const ASFED_MAX = rAsFed?.max !== undefined ? toNum(rAsFed.max) : undefined;

    const coefs = mats.map((m) => {
        const ms = MSfr(m);
        return {
            EM: ms * get(m, "EM", 0),
            NDT: ms * (get(m, "NDT", 0) / 100),
            PB: ms * (get(m, "PB", 0) / 100) * 1000,
            Ca: ms * (get(m, "Ca", 0) / 100) * 1000,
            P: ms * (get(m, "P", 0) / 100) * 1000,
            Mg: ms * (get(m, "Mg", 0) / 100) * 1000,
            Na: ms * (get(m, "Na", 0) / 100) * 1000,
            K: ms * (get(m, "K", 0) / 100) * 1000,
            S: ms * (get(m, "S", 0) / 100) * 1000,
            Cu: ms * get(m, "Cu", 0),
            Co: ms * get(m, "Co", 0),
            Fe: ms * get(m, "Fe", 0),
            Mn: ms * get(m, "Mn", 0),
            Se: ms * get(m, "Se", 0),
            Zn: ms * get(m, "Zn", 0),
            I: ms * get(m, "I", 0),
            FDN: ms * (get(m, "FDN", 0) / 100),
            EE: ms * (get(m, "EE", 0) / 100),
            CNF: ms * (get(m, "CNF", 0) / 100),
            MS: ms,
        };
    });

    const C = [];
    const constrA = [];
    const constrB = [];

    // Min Σ (a_i * costo_i)
    for (let i = 0; i < n; i++) C.push(getCosto(mats[i].id));

    // No negatividad
    for (let i = 0; i < n; i++) {
        const row = Array(n).fill(0); row[i] = -1;
        addLeq(constrA, constrB, { A: row, b: 0 });
    }

    // Total as-fed (Req ID=1 si viene)
    const sumAsFed = Array(n).fill(1);
    if (ASFED_MIN !== undefined) addGeq(constrA, constrB, { A: sumAsFed, b: ASFED_MIN });
    if (ASFED_MAX !== undefined) addLeq(constrA, constrB, { A: sumAsFed, b: ASFED_MAX });

    // Restricciones por materia en KG as-fed
    for (let i = 0; i < n; i++) {
        const r = restrById[String(mats[i].id)] || {};
        const minKg = toNum(r.minKg ?? r.min_kg ?? r.minAsFed ?? r.min ?? r.kgMin);
        const maxKg = toNum(r.maxKg ?? r.max_kg ?? r.maxAsFed ?? r.max ?? r.kgMax);

        if (minKg !== undefined && minKg > 0) {
            const row = Array(n).fill(0); row[i] = -1;     // -a_i <= -minKg
            addLeq(constrA, constrB, { A: row, b: -minKg });
        }
        if (maxKg !== undefined && maxKg >= 0) {
            const row = Array(n).fill(0); row[i] = 1;      //  a_i <= maxKg
            addLeq(constrA, constrB, { A: row, b: maxKg });
        }
    }

    // Requisitos por ID
    const id2name = {
        2: "EM", 3: "NDT", 4: "PB", 5: "PB",
        6: "Ca", 7: "P", 8: "Mg", 9: "Na", 10: "K", 11: "S",
        12: "Cu", 13: "Co", 14: "Fe", 15: "Mn", 16: "Se", 17: "Zn", 18: "I",
        19: "FDN", 20: "EE", 21: "CNF"
    };

    function addNutReq(id, min, max) {
        const name = id2name[id]; if (!name) return;
        const row = Array(n).fill(0).map((_, i) => coefs[i][name] ?? 0);

        if (min !== undefined) {
            const rneg = row.map(v => -v);
            addLeq(constrA, constrB, { A: rneg, b: -min });
        }
        if (max !== undefined) {
            addLeq(constrA, constrB, { A: row, b: max });
        }
    }
    for (const r of rowsReq) {
        const id = toNum(r.id);
        if (!id || id === 1) continue;
        addNutReq(id, toNum(r.min), toNum(r.max));
    }

    // Resolver
    const sol = simplexMin(constrA, constrB, C);
    if (!sol.ok) {
        return { ok: false, msg: sol.msg || "No óptimo (LP)", porMateria: {}, totales: {}, reqObtenidoById: {}, estadoById: {} };
    }

    // Resultados
    const EPS = 1e-9;
    const a = sol.x.map(v => (v < EPS ? 0 : v));

    const porMateria = {};
    const tot = {
        ap_mstotal: 0,
        materiaapvinculaciontotal: 0,
        ap_emtotal: 0, ap_ndttotal: 0, ap_pbtotal: 0,
        ap_catotal: 0, ap_ptotal: 0, ap_mgtotal: 0, ap_natotal: 0, ap_ktotal: 0, ap_stotal: 0,
        ap_cutotal: 0, ap_cototal: 0, ap_fetotal: 0, ap_mntotal: 0, ap_setotal: 0, ap_zntotal: 0, ap_itotal: 0,
        ap_fdntotal: 0, ap_eetotal: 0, ap_cnftotal: 0,
        costo_total: 0,
    };

    for (let i = 0; i < n; i++) {
        const m = mats[i];
        const ms = MSfr(m);
        const ai = a[i] || 0;
        const AP = ai * ms;
        const c = coefs[i];
        const costoU = getCosto(m.id);

        porMateria[m.id] = {
            AP_vinculacion: AP,            // kg MS/d
            materiaapvinculacion: ai,      // kg as-fed/d
            costoDia: ai * costoU,
            aportes: {
                ap_em: ai * c.EM,
                ap_ndt: ai * c.NDT,
                ap_pb: ai * c.PB,
                ap_ca: ai * c.Ca,
                ap_p: ai * c.P,
                ap_mg: ai * c.Mg,
                ap_na: ai * c.Na,
                ap_k: ai * c.K,
                ap_s: ai * c.S,
                ap_cu: ai * c.Cu,
                ap_co: ai * c.Co,
                ap_fe: ai * c.Fe,
                ap_mn: ai * c.Mn,
                ap_se: ai * c.Se,
                ap_zn: ai * c.Zn,
                ap_i: ai * c.I,
                ap_fdn: ai * c.FDN,
                ap_ee: ai * c.EE,
                ap_cnf: ai * c.CNF,
            }
        };

        tot.ap_mstotal += AP;
        tot.materiaapvinculaciontotal += ai;
        tot.ap_emtotal += ai * c.EM;
        tot.ap_ndttotal += ai * c.NDT;
        tot.ap_pbtotal += ai * c.PB;
        tot.ap_catotal += ai * c.Ca;
        tot.ap_ptotal += ai * c.P;
        tot.ap_mgtotal += ai * c.Mg;
        tot.ap_natotal += ai * c.Na;
        tot.ap_ktotal += ai * c.K;
        tot.ap_stotal += ai * c.S;
        tot.ap_cutotal += ai * c.Cu;
        tot.ap_cototal += ai * c.Co;
        tot.ap_fetotal += ai * c.Fe;
        tot.ap_mntotal += ai * c.Mn;
        tot.ap_setotal += ai * c.Se;
        tot.ap_zntotal += ai * c.Zn;
        tot.ap_itotal += ai * c.I;
        tot.ap_fdntotal += ai * c.FDN;
        tot.ap_eetotal += ai * c.EE;
        tot.ap_cnftotal += ai * c.CNF;
        tot.costo_total += ai * costoU;
    }

    const reqObtenidoById = {
        1: tot.materiaapvinculaciontotal,
        2: tot.ap_emtotal,
        3: tot.ap_ndttotal,
        4: tot.ap_pbtotal,
        5: tot.ap_pbtotal,
        6: tot.ap_catotal,
        7: tot.ap_ptotal,
        8: tot.ap_mgtotal,
        9: tot.ap_natotal,
        10: tot.ap_ktotal,
        11: tot.ap_stotal,
        12: tot.ap_cutotal,
        13: tot.ap_cototal,
        14: tot.ap_fetotal,
        15: tot.ap_mntotal,
        16: tot.ap_setotal,
        17: tot.ap_zntotal,
        18: tot.ap_itotal,
        19: tot.ap_fdntotal,
        20: tot.ap_eetotal,
        21: tot.ap_cnftotal,
    };

    const estadoById = {};
    for (const r of rowsReq) {
        const id = toNum(r.id);
        const val = reqObtenidoById[id];
        const min = toNum(r.min);
        const max = toNum(r.max);
        if (val === undefined) { estadoById[id] = "—"; continue; }
        if (min !== undefined && val < min) { estadoById[id] = "BAJO"; continue; }
        if (max !== undefined && val > max) { estadoById[id] = "ALTO"; continue; }
        estadoById[id] = "OK";
    }

    return { ok: true, msg: "OK", porMateria, totales: tot, reqObtenidoById, estadoById };
}

export { optimizarDietaLP };
export default optimizarDietaLP;
